import { VtkRenderWindowParentContext } from '@/src/components/vtk/context';
import { onVTKEvent } from '@/src/composables/onVTKEvent';
import { View } from '@/src/core/vtk/types';
import { Maybe } from '@/src/types';
import { VtkRenderWindowParentApi } from '@/src/types/vtk-types';
import { releaseOpenGLRenderWindow } from '@/src/core/vtk/releaseRenderWindow';
import { deleteInteractor } from '@/src/core/vtk/deleteInteractor';
import { batchForNextTask } from '@/src/utils/batchForNextTask';
import vtkRenderWindow from '@kitware/vtk.js/Rendering/Core/RenderWindow';
import vtkRenderWindowInteractor from '@kitware/vtk.js/Rendering/Core/RenderWindowInteractor';
import vtkRenderer from '@kitware/vtk.js/Rendering/Core/Renderer';
import vtkOpenGLRenderWindow from '@kitware/vtk.js/Rendering/OpenGL/RenderWindow';
import vtkWidgetManager from '@kitware/vtk.js/Widgets/Core/WidgetManager';
import type { Bounds } from '@kitware/vtk.js/types';
import { useElementSize } from '@vueuse/core';
import {
  inject,
  MaybeRef,
  onScopeDispose,
  unref,
  watch,
  watchPostEffect,
} from 'vue';

export function useWebGLRenderWindow(
  renderWindow: vtkRenderWindow,
  container: MaybeRef<Maybe<HTMLElement>>,
  parent: Maybe<VtkRenderWindowParentApi>
) {
  const renderWindowView =
    (parent?.renderWindowView.addMissingNode(renderWindow) as
      | vtkOpenGLRenderWindow
      | undefined) ?? vtkOpenGLRenderWindow.newInstance();

  watchPostEffect((onCleanup) => {
    const el = unref(container);
    if (!el) return;

    renderWindowView.setContainer(el);
    onCleanup(() => {
      renderWindowView.setContainer(null);
    });
  });

  onScopeDispose(() => {
    // a child node draws through its parent's context, so only the standalone
    // case has one of its own to release
    if (parent?.renderWindowView) {
      parent.renderWindowView.removeNode(renderWindowView);
    } else {
      releaseOpenGLRenderWindow(renderWindowView);
    }
  });

  renderWindow.addView(renderWindowView);

  onScopeDispose(() => {
    renderWindow.removeView(renderWindowView);
  });

  return renderWindowView;
}

export function useWidgetManager(renderer: vtkRenderer) {
  const manager = vtkWidgetManager.newInstance();
  manager.setRenderer(renderer);

  onScopeDispose(() => manager.delete());

  const updatePickingState = () => {
    const enabled = manager.getPickingEnabled();
    const widgets = manager.getWidgets();
    const focused = widgets.find((widget) => widget.hasFocus());
    const needsPicking = focused
      ? focused.getNestedPickable()
      : widgets.some((widget) => widget.getNestedPickable());
    if (!enabled && needsPicking) {
      manager.enablePicking();
    } else if (enabled && !needsPicking) {
      manager.disablePicking();
    }
  };

  onVTKEvent(manager, 'onModified', updatePickingState);
  updatePickingState();

  return manager;
}

type VtkViewOptions = {
  separateImageLayer?: boolean;
  clippingBounds?: MaybeRef<Bounds>;
};

export function useVtkView(
  container: MaybeRef<Maybe<HTMLElement>>,
  { separateImageLayer = false, clippingBounds }: VtkViewOptions = {}
): View {
  const parent = inject(VtkRenderWindowParentContext, null);
  const renderer = vtkRenderer.newInstance();
  const renderWindow = vtkRenderWindow.newInstance();
  renderWindow.addRenderer(renderer);

  // Separate passes composite segmentations and widgets over the image layers,
  // while retaining order-independent transparency within each renderer.
  const imageRenderer = separateImageLayer
    ? vtkRenderer.newInstance({ interactive: false })
    : renderer;
  if (separateImageLayer) {
    imageRenderer.setActiveCamera(renderer.getActiveCamera());
    imageRenderer.setBackground(0, 0, 0);
    renderer.setLayer(1);
    renderWindow.setNumberOfLayers(2);
    renderWindow.addRenderer(imageRenderer);
  }

  let disposed = false;

  // Registered before the view cleanup because the parent rebuilds its scene
  // graph from its own child render window list.
  onScopeDispose(() => {
    disposed = true;
    parent?.renderWindow.removeRenderWindow(renderWindow);
  });

  parent?.renderWindow.addRenderWindow(renderWindow);

  // the render window view
  const renderWindowView = useWebGLRenderWindow(
    renderWindow,
    container,
    parent
  );

  // interactor
  const interactor = vtkRenderWindowInteractor.newInstance();
  renderWindow.setInteractor(interactor);
  interactor.setView(renderWindowView);

  watchPostEffect((onCleanup) => {
    const el = unref(container);
    if (!el) return;

    interactor.initialize();
    interactor.setContainer(el);
    onCleanup(() => {
      if (interactor.getContainer()) interactor.unbindEvents();
    });
  });

  // widget manager
  const widgetManager = useWidgetManager(renderer);

  // render API
  const deferredRender = batchForNextTask(() => {
    // don't need to re-render during animation
    if (interactor.isAnimating()) return;
    widgetManager.renderWidgets();
    renderWindow.render();
  });

  const immediateRender = () => {
    if (interactor.isAnimating()) return;
    parent?.renderWindow.render();
    renderWindow.render();
  };

  // requests outlive the view: vtk event handlers and promises that settle
  // after dispose has deleted the scene graph nodes a render would traverse
  const requestRender = ({ immediate } = { immediate: false }) => {
    if (disposed) return;
    if (immediate) {
      immediateRender();
    }
    deferredRender();
  };

  onVTKEvent(renderer, 'onModified', () => {
    requestRender();
  });
  if (separateImageLayer) {
    onVTKEvent(imageRenderer, 'onModified', () => {
      requestRender();
    });
  }

  // set size
  const setSize = (width: number, height: number) => {
    // ensure we have a non-zero size, otherwise
    // the framebuffers might not be populated correctly
    const scaledWidth = Math.max(1, width * globalThis.devicePixelRatio);
    const scaledHeight = Math.max(1, height * globalThis.devicePixelRatio);
    renderWindowView.setSize(scaledWidth, scaledHeight);
    imageRenderer.resetCameraClippingRange(unref(clippingBounds));

    parent?.renderWindowView.resizeFromChildRenderWindows();
    parent?.renderWindow.render();
    requestRender({ immediate: true });
  };

  const { width, height } = useElementSize(container);
  watch([width, height], ([w, h]) => setSize(w, h), { immediate: true });

  // cleanup
  onScopeDispose(() => {
    deferredRender.cancel();

    renderWindow.removeRenderer(renderer);
    if (separateImageLayer) {
      renderWindow.removeRenderer(imageRenderer);
      imageRenderer.delete();
    }

    renderer.delete();
    renderWindow.delete();
    deleteInteractor(interactor);
  });

  return {
    renderer,
    imageRenderer,
    renderWindow,
    interactor,
    renderWindowView,
    widgetManager,
    requestRender,
  };
}
